// Copyright 2016 Sorama B.V.

import { autoinject, bindable, bindingMode, computedFrom, customElement, Disposable } from "aurelia-framework";
import { TaskQueue } from "aurelia-task-queue";
import { Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotParentBase } from "controls/plot/PlotParentBase";
import { Point2D } from "datatypes/SoramaWebApi";
import { StringExtensions } from "extensions/StringExtensions";
import { IExportable } from "libraries/Export/IExportable";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { SVGNode } from "libraries/SVG/SVGNode";
import { IColorMap } from "services/ColorMap/IColorMap";
import { Generic } from "units/Generic";
import { Unit, UnitValue } from "units/Unit";
import { IGridData } from "./GridData";

// Shaders
import GridPlotFragmentShader from "../shaders/GridPlotFragmentShader.shader";
import GridPlotVertexShader from "../shaders/GridPlotVertexShader.shader";

@customElement("grid-plot")
// @containerless()
@autoinject
export class GridPlot extends PlotParentBase implements IExportable {
    //#region Injected Services
    private readonly taskQueue: TaskQueue;
    private readonly resizeListener: ResizeListener;
    //#endregion

    //#region Preallocated arrays
    private colormapTextureDataArray = new Uint8Array(GridPlot.ColorMapResolution * 4);
    private dataTextureDataArray: Uint8Array;

    //#endregion

    //#region Constants
    private static ColorMapResolution = 256;
    //#endregion

    // #region Bindables
    @bindable({ changeHandler: "onDataChanged" })
    public data: IGridData;

    @bindable({ changeHandler: "onColorMapChanged" })
    public colormap: IColorMap;

    @bindable({ changeHandler: "onDisplayUnitChanged" })
    public displayUnit: Unit = Generic.unitless;

    @bindable
    public interpolate: boolean;

    @bindable({ changeHandler: "onAlphaValueChanged" })
    public opacity = 1;

    @bindable({ changeHandler: "onAlphaValueChanged" })
    public transparency = 0;

    @bindable({ changeHandler: "onAlphaValueChanged" })
    public zeroCenteredData = false;

    @bindable({ changeHandler: "onFixedMinMaxChanged" })
    public fixedMinimum: UnitValue;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public dataMinimum: UnitValue = new UnitValue(0, Generic.unitless);

    @bindable({ changeHandler: "onFixedMinMaxChanged" })
    public fixedMaximum: UnitValue;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public dataMaximum: UnitValue = new UnitValue(1, Generic.unitless);

    @bindable
    public allowFullscreen: boolean;
    //#endregion

    //#region WebGL fields

    private gl: WebGLRenderingContext;
    private vertexDataLocation: number;
    private texCoordDataLocation: number;
    private hologramTextureLocation: WebGLUniformLocation;
    private colormapTextureLocation: WebGLUniformLocation;
    private shaderViewportLocation: WebGLUniformLocation;

    private vertexBuffer: WebGLBuffer;
    private texCoordBuffer: WebGLBuffer;

    private colorMapTexture: WebGLTexture;
    private hologramTexture: WebGLTexture;
    private shaderProgram: WebGLProgram;

    //#endregion

    //#region HTML Elements

    private hologramCanvasNode: HTMLElement;
    private partsContainer: HTMLElement;

    //#endregion

    //#region Fields

    private subscriptions: Disposable[];

    @computedFrom("aspectSize")
    private get aspectRatio(): number {
        return this.aspectSize.width / this.aspectSize.height;
    }

    @computedFrom("bufferedGridSize")
    private get aspectSize(): Size {
        return this.bufferedGridSize ? new Size(this.bufferedGridSize.x, this.bufferedGridSize.y) : new Size(16, 9);
    }

    @computedFrom("data")
    protected get hasData(): boolean {
        return this.data !== undefined && this.data !== null;
    }

    private bufferedGridDimension: Point2D;
    private bufferedGridSize: Point2D;
    private bufferedGridUnit: Unit;

    private currentMinimum: number;
    private currentMaximum: number;

    private isAttached = false;
    private isFullScreen = false;

    // #endregion

    //#region Constructor and Initialisation

    constructor(resizeListener: ResizeListener, taskQueue: TaskQueue) {
        super();
        this.resizeListener = resizeListener;
        this.taskQueue = taskQueue;
        this.subscriptions = [];
    }

    public bind(): void {
        // Use the aspect ratio measurement element to determine our size
        this.subscriptions.push(
            this.resizeListener.elementResizeEvent(this.controlContainer, () => this.requestMeasure())
        );

        if (!this.initWebGl()) {
            console.error("Cannot initialise WebGL");
            return;
        }

        if (!this.initShaders()) {
            console.error("Cannot initialise shaders");
            return;
        }

        if (!this.initTextures()) {
            console.error("Cannot initialise textures");
            return;
        }

        if (!this.initBuffers()) {
            console.error("Cannot initialise buffer");
        }

        if (!this.initBlending()) {
            console.error("Cannot initialise blending");
        }

        if (this.colormap) {
            // Update colormap related data
            this.updateColorMapTexture();
            this.updateClearColor();
        }

        if (this.data) {
            // Trigger data update if data is defined
            this.onDataChanged(this.data, undefined);
        }

        document.addEventListener("fullscreenchange", this.fullscreenChanged);
    }

    public attached(): void {
        this.isAttached = true;

        // Wait for the task queue to be complete and then force a redraw
        this.taskQueue.queueTask(() => {
            // Measure the control
            this.requestMeasure();

            this.requestRedraw();
        });
    }

    public detached(): void {
        this.isAttached = false;
    }

    public unbind(): void {
        document.removeEventListener("fullscreenchange", this.fullscreenChanged);

        this.subscriptions.forEach((x) => x.dispose());

        // Cleanup opengl stuff
        this.gl.deleteBuffer(this.vertexBuffer);
        this.gl.deleteBuffer(this.texCoordBuffer);

        this.gl.deleteTexture(this.hologramTexture);
        this.gl.deleteTexture(this.colorMapTexture);

        this.gl.deleteProgram(this.shaderProgram);

        this.gl = null;
    }

    //#endregion

    //#region IPlotParent

    public requestMeasure(force = false): void {
        if (!this.isAttached) return;

        // Measure the control
        const redraw = this.measureControl(force);
        if (!force && !redraw) {
            return; // No change in measure, skip the update
        }

        // Since we always enforce a square aspect ratio (since SVG linewidth = 1px is not supported everywhere) we need to measure the viewport as well
        this.measureViewPort();

        // Redraw
        this.requestRedraw();
    }

    public requestViewPort(): void {
        this.measureViewPort();
    }

    public getAxisBinSizes(): Pair<number> {
        return <Pair<number>>{
            x: this.data.grid.spacing.x,
            y: this.data.grid.spacing.y,
        };
    }

    public roundToNearestBin(value: number, axis: string): number {
        if (this.data) {
            if (axis === "x") {
                const binsize = this.data.grid.spacing.x;
                const bins = this.data.grid.dimension.x;
                const start = this.data.grid.center.x - (binsize * bins) / 2;

                const bin = Math.round((value - start) / binsize);

                return start + bin * binsize;
            } else if (axis === "y") {
                const binsize = this.data.grid.spacing.y;
                const bins = this.data.grid.dimension.y;
                const start = this.data.grid.center.y - (binsize * bins) / 2;

                const bin = Math.round((value - start) / binsize);

                return start + bin * binsize;
            }
        }

        return value;
    }

    //#endregion

    //#region Initialise Functions

    private initWebGl(): boolean {
        const canvas = <HTMLCanvasElement>this.hologramCanvasNode;
        this.gl = <WebGLRenderingContext>canvas.getContext("webgl", { preserveDrawingBuffer: true });
        if (!this.gl) {
            this.gl = <WebGLRenderingContext>canvas.getContext("experimental-webgl", { preserveDrawingBuffer: true });
        }
        if (!this.gl) {
            return false;
        }

        this.updateClearColor();

        return true;
    }

    private initShaders(): boolean {
        let error: unknown;

        // Generate shaders
        const vertexShader = this.gl.createShader(this.gl.VERTEX_SHADER);
        const fragmentShader = this.gl.createShader(this.gl.FRAGMENT_SHADER);

        // Compile shaders
        this.gl.shaderSource(vertexShader, GridPlotVertexShader);
        this.gl.compileShader(vertexShader);

        if (!this.gl.getShaderParameter(vertexShader, this.gl.COMPILE_STATUS)) {
            error = this.gl.getShaderInfoLog(vertexShader);
            console.error(error);
            return false;
        }

        this.gl.shaderSource(fragmentShader, GridPlotFragmentShader);
        this.gl.compileShader(fragmentShader);

        if (!this.gl.getShaderParameter(fragmentShader, this.gl.COMPILE_STATUS)) {
            error = this.gl.getShaderInfoLog(fragmentShader);
            console.error(error);
            return false;
        }

        // Create program
        this.shaderProgram = this.gl.createProgram();
        this.gl.attachShader(this.shaderProgram, vertexShader);
        this.gl.attachShader(this.shaderProgram, fragmentShader);
        this.gl.linkProgram(this.shaderProgram);

        if (!this.gl.getProgramParameter(this.shaderProgram, this.gl.LINK_STATUS)) {
            error = this.gl.getProgramInfoLog(this.shaderProgram);
            console.error(error);
            return null;
        }

        this.vertexDataLocation = this.gl.getAttribLocation(this.shaderProgram, "rm_Vertex");
        this.texCoordDataLocation = this.gl.getAttribLocation(this.shaderProgram, "texCoord");
        this.hologramTextureLocation = this.gl.getUniformLocation(this.shaderProgram, "tex");
        this.colormapTextureLocation = this.gl.getUniformLocation(this.shaderProgram, "colormapTexture");
        this.shaderViewportLocation = this.gl.getUniformLocation(this.shaderProgram, "viewPort");
        return true;
    }

    private initBuffers(): boolean {
        this.vertexBuffer = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer);

        // Already set the vertices since this is fixed
        const vertexArray = new Float32Array(8);
        vertexArray[0] = -1;
        vertexArray[1] = 1;
        vertexArray[2] = -1;
        vertexArray[3] = -1;
        vertexArray[4] = 1;
        vertexArray[5] = 1;
        vertexArray[6] = 1;
        vertexArray[7] = -1;

        this.gl.bufferData(this.gl.ARRAY_BUFFER, vertexArray, this.gl.STATIC_DRAW);

        this.texCoordBuffer = this.gl.createBuffer();

        return true;
    }

    private initTextures(): boolean {
        this.hologramTexture = this.gl.createTexture();
        this.colorMapTexture = this.gl.createTexture();

        this.updateDataTexture();
        this.updateColorMapTexture();

        return true;
    }

    private initBlending(): boolean {
        this.gl.disable(this.gl.BLEND);
        return true;
    }

    //#endregion

    //#region Change Handlers

    private measureControl(force: boolean): boolean {
        if (
            !this.controlContainer ||
            (!force &&
                this.controlContainer.clientWidth === this.size.width &&
                this.controlContainer.clientHeight === this.size.height) ||
            (this.controlContainer.clientWidth === 0 && this.controlContainer.clientHeight === 0)
        ) {
            // Not initialised or no change, no need to measure
            return false;
        }

        // Measure children
        const margin = new Margin(0, 0, 0, 0);

        this.plotParts.forEach((x) => {
            const partMargin = x.onMeasureMargin();

            margin.left = Math.max(margin.left, partMargin.left);
            margin.top = Math.max(margin.top, partMargin.top);
            margin.right = Math.max(margin.right, partMargin.right);
            margin.bottom = Math.max(margin.bottom, partMargin.bottom);
        });

        // Make sure we respect the aspect ratio within the given size
        const availableWidth = Math.max(this.controlContainer.clientWidth - margin.left - margin.right, 0);
        const availableHeight = Math.max(this.controlContainer.clientHeight - margin.top - margin.bottom, 0);

        const size1 = new Size(availableWidth, availableWidth / this.aspectRatio);
        const size2 = new Size(availableHeight * this.aspectRatio, availableHeight);

        const size = size1.height > availableHeight ? size2 : size1;
        this.size = new Size(size.width + margin.left + margin.right, size.height + margin.top + margin.bottom);

        this.margin = margin;

        // Update webgl viewport
        if (this.gl) {
            this.gl.viewport(
                0,
                0,
                this.size.width - this.margin.left - this.margin.right,
                this.size.height - this.margin.top - this.margin.bottom
            );
        }

        // Notify children of size and margin change
        this.plotParts.forEach((x) => {
            x.onSize(this.size, this.margin);
        });

        return true;
    }

    private measureViewPort(dataChanged?: boolean): void {
        if (!this.bufferedGridSize || !this.data) return;

        this.viewPortBounds = new Rect(
            -this.bufferedGridSize.x / 2 + this.data.grid.center.x,
            -this.bufferedGridSize.y / 2 + this.data.grid.center.y,
            this.bufferedGridSize.x,
            this.bufferedGridSize.y
        );

        if (dataChanged) {
            this.xAxisStart = this.viewPortBounds.x;
            this.yAxisStart = this.viewPortBounds.y;
            this.xAxisEnd = this.viewPortBounds.width + this.viewPortBounds.x;
            this.yAxisEnd = this.viewPortBounds.height + this.viewPortBounds.y;
        }

        this.viewPort = new Rect(
            this.xAxisStart,
            this.yAxisStart,
            this.xAxisEnd - this.xAxisStart,
            this.yAxisEnd - this.yAxisStart
        );

        this.plotParts.forEach((x) => {
            x.onViewPortComputed(
                this.viewPort,
                this.viewPortBounds,
                new Pair<Unit>(this.bufferedGridUnit, this.bufferedGridUnit)
            );
        });

        this.updateDataTexture();
        this.requestRedraw();
    }

    public onDataChanged(newData: IGridData, oldData: IGridData) {
        if (!this.gl || !newData) {
            this.dataMinimum = undefined;
            this.dataMaximum = undefined;
            this.requestRedraw();
            return;
        }

        const gridSize = newData.grid.size();
        // Check if we need to remeasure the control
        if (
            !this.bufferedGridSize ||
            this.bufferedGridSize.x !== gridSize.x ||
            this.bufferedGridSize.y !== gridSize.y ||
            this.bufferedGridUnit !== newData.grid.unit
        ) {
            this.bufferedGridSize = gridSize;
            this.bufferedGridUnit = newData.grid.unit;
            this.measureViewPort(true);
        }
        // Update hologram texture
        this.updateDataTexture();

        const grid = this.data.grid;
        if (
            this.bufferedGridDimension &&
            this.bufferedGridDimension.x === grid.dimension.x &&
            this.bufferedGridDimension.y === grid.dimension.y
        ) {
            // Request a redraw
            this.requestRedraw();
            return;
        }

        this.bufferedGridDimension = grid.dimension;
        this.measureControl(true);
        this.updateTexelBuffer();

        // Request a redraw
        this.requestRedraw();
    }

    public onDisplayUnitChanged(newUnit: Unit, oldUnit: Unit) {
        this.updateColorMapTexture();

        // Update clear color
        this.updateClearColor();
        // Request a redraw
        this.requestRedraw();
    }

    public onColorMapChanged(oldColorMap: IColorMap, newColorMap: IColorMap) {
        if (!this.gl) return;

        // Update colormap texture
        this.updateColorMapTexture();

        // Update clear color
        this.updateClearColor();

        // Request a redraw
        this.requestRedraw();
    }

    public onAlphaValueChanged(newValue: number, oldValue: number) {
        if (newValue === undefined || newValue === null) return;

        this.updateClearColor();

        this.updateColorMapTexture();

        // Request a redraw
        this.requestRedraw();
    }

    public onFixedMinMaxChanged(): void {
        this.updateDataTexture();
        this.requestRedraw();
    }

    private fullscreenChanged = (ev: Event) => {
        this.isFullScreen = !!document.fullscreenElement;
    };

    //#endregion

    //#region IExportable

    public exportToSvg(suggestedWidth?: number, suggestedHeight?: number): Promise<SVGNode> {
        const svgNode = new SVGNode("svg");
        svgNode.setAttribute(
            "viewBox",
            StringExtensions.format(
                "{0},{1},{2},{3}",
                (-this.margin.left).toFixed(0),
                (-this.margin.top).toFixed(0),
                this.size.width.toFixed(0),
                this.size.height.toFixed(0)
            )
        );
        svgNode.setAttribute("shape-rendering", "geometricPrecision");

        const svgSize = {
            width: this.size.width - this.margin.left - this.margin.right,
            height: this.size.height - this.margin.top - this.margin.bottom,
        };

        const time = new Date().getTime();
        const clipPathId = "svgclip" + time;

        // Generate a defs section with a clip path
        const defsNode = new SVGNode("defs");
        const clipPathNode = new SVGNode("clipPath");
        clipPathNode.setAttribute("id", clipPathId);

        const rectNode = new SVGNode("rect");
        rectNode.setAttribute("x", "0");
        rectNode.setAttribute("y", "0");
        rectNode.setAttribute("width", svgSize.width.toFixed(0));
        rectNode.setAttribute("height", svgSize.height.toFixed(0));

        clipPathNode.addChild(rectNode);
        defsNode.addChild(clipPathNode);
        svgNode.addChild(defsNode);

        // Add the waterfall image nodes
        this.redraw();
        const spectrogram = (<HTMLCanvasElement>this.hologramCanvasNode).toDataURL("image/png");
        const mainNode = new SVGNode("g");
        mainNode.setAttribute("clip-path", "url(#" + clipPathId + ")");

        const imageNode = new SVGNode("image");
        imageNode.setAttribute("x", "0");
        imageNode.setAttribute("y", "0");
        imageNode.setAttribute("width", svgSize.width.toFixed(0));
        imageNode.setAttribute("height", svgSize.height.toFixed(0));
        imageNode.setAttribute("xlink:href", spectrogram);

        mainNode.addChild(imageNode);

        // Order children on drawlevel
        const children: { level: number; node: SVGNode }[] = [];
        children.push({ level: 0, node: mainNode });

        // For each plot part, check if they implement exportToSvg
        const partPromises = this.plotParts.map((part) => {
            const exportablePart = <IExportable>(<unknown>part);
            if (!exportablePart.exportToSvg) return Promise.resolve(undefined);

            return exportablePart.exportToSvg(suggestedWidth, suggestedHeight).then((partSvgNode) => {
                if (partSvgNode) {
                    // Remove SVG parent and replace with g
                    const gNode = new SVGNode("g");
                    for (const child of partSvgNode.children) {
                        gNode.addChild(child);
                    }
                    children.push({ level: part.drawLayer, node: gNode });
                }
            });
        });

        return Promise.all(partPromises).then(() => {
            // Order nodes and add them to the svg
            for (const child of children.sort((a, b) => a.level - b.level)) {
                svgNode.addChild(child.node);
            }

            return svgNode;
        });
    }

    //#endregion

    //#region Update and Redraw Functions

    private updateClearColor(): void {
        if (!this.gl) return;

        if (this.colormap && this.transparency <= 0) {
            const clearColor = this.colormap.toColor(0);
            this.gl.clearColor(
                clearColor.r * this.opacity,
                clearColor.g * this.opacity,
                clearColor.b * this.opacity,
                clearColor.a * this.opacity
            );
        } else {
            this.gl.clearColor(0, 0, 0, this.transparency <= 0 ? this.opacity : 0);
        }

        this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }

    private updateTexelBuffer(): void {
        const grid = this.data.grid;

        // Bind the correct buffer
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);

        // Compute coordinates
        const textureWidth = Math.pow(2, Math.ceil(Math.log2(grid.dimension.x)));
        const textureHeight = Math.pow(2, Math.ceil(Math.log2(grid.dimension.y)));

        const tx = grid.dimension.x / textureWidth;
        const ty = grid.dimension.y / textureHeight;

        const texArray = new Float32Array(8);
        texArray[0] = 0;
        texArray[1] = 0;
        texArray[2] = 0;
        texArray[3] = ty;
        texArray[4] = tx;
        texArray[5] = 0;
        texArray[6] = tx;
        texArray[7] = ty;

        this.gl.bufferData(this.gl.ARRAY_BUFFER, texArray, this.gl.STATIC_DRAW);
    }

    private updateDataTexture(): void {
        if (!this.data) return;

        // First create the hologram array for the texture
        const gy = this.data.grid.dimension.y;
        const gx = this.data.grid.dimension.x;

        // Determine min and max values
        const minmax = this.data.getMinMax(this.viewPort, this.data.grid.unit);
        if (this.fixedMinimum && this.fixedMinimum.unit.isOfSameUnitFamily(this.data.unit)) {
            minmax.min = this.fixedMinimum.toUnit(this.data.unit);

            // Make sure max does not go below fixed min
            minmax.max = Math.max(minmax.max, minmax.min + Number.EPSILON);
        }
        if (this.fixedMaximum && this.fixedMaximum.unit.isOfSameUnitFamily(this.data.unit)) {
            minmax.max = this.fixedMaximum.toUnit(this.data.unit);

            // Make sure min does not go above fixed max
            minmax.min = Math.min(minmax.min, minmax.max - Number.EPSILON);
        }

        const diff = minmax.max - minmax.min;

        const textureWidth = Math.pow(2, Math.ceil(Math.log2(gx)));
        const textureHeight = Math.pow(2, Math.ceil(Math.log2(gy)));

        // Check if there is a preallocated array available for texture data and if it is big enough
        if (!this.dataTextureDataArray || this.dataTextureDataArray.length < textureWidth * textureHeight * 4) {
            this.dataTextureDataArray = new Uint8Array(textureWidth * textureHeight * 4);
        }

        let offset = 0;
        let gOffset = (gy - 1) * gx;
        for (let y = 0; y < gy; y++) {
            for (let x = 0; x < gx; x++) {
                const dataValue = this.data.data[gOffset++];

                const normalizedValue = Math.max(Math.min((dataValue - minmax.min) / diff, 1), 0);

                const value = normalizedValue * 65535;
                this.dataTextureDataArray[offset++] = (value & 0x0000ff00) >> 8;
                this.dataTextureDataArray[offset++] = value & 0x000000ff;
                offset += 2;

                if (textureWidth !== gx && x === gx - 1) {
                    // Repeat the last row to prevent interpolation effects
                    this.dataTextureDataArray[(y * textureWidth + x + 1) * 4] =
                        this.dataTextureDataArray[(y * textureWidth + x) * 4];
                    this.dataTextureDataArray[(y * textureWidth + x + 1) * 4 + 1] =
                        this.dataTextureDataArray[(y * textureWidth + x) * 4];
                }
            }

            if (textureHeight !== gy && y === gy - 1) {
                // Repeat the last row to prevent interpolation effects
                for (let x = 0; x < gx; x++) {
                    this.dataTextureDataArray[((y + 1) * textureWidth + x) * 4] =
                        this.dataTextureDataArray[(y * textureWidth + x) * 4];
                    this.dataTextureDataArray[((y + 1) * textureWidth + x) * 4 + 1] =
                        this.dataTextureDataArray[(y * textureWidth + x) * 4];
                }
            }
            offset += (textureWidth - gx) * 4;
            gOffset -= gx + gx;
        }

        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.hologramTexture);
        this.gl.texImage2D(
            WebGLRenderingContext.TEXTURE_2D,
            0,
            this.gl.RGBA,
            textureWidth,
            textureHeight,
            0,
            this.gl.RGBA,
            this.gl.UNSIGNED_BYTE,
            this.dataTextureDataArray
        );

        if (this.interpolate) {
            this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);
            this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);

            this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
            this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
        } else {
            this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
            this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);

            this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
            this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
        }

        this.gl.generateMipmap(WebGLRenderingContext.TEXTURE_2D);

        this.currentMaximum = minmax.max;
        this.currentMinimum = minmax.min;
    }

    private updateColorMapTexture(): void {
        if (!this.colormap) return;

        // Create data for texture
        const colorData = this.colormap.toColorRange(GridPlot.ColorMapResolution);
        for (let i = 0; i < GridPlot.ColorMapResolution; i++) {
            const value = i / (GridPlot.ColorMapResolution - 1);
            const transValue = this.zeroCenteredData ? Math.abs(value - 0.5) * 2 : value;

            const transparencyCorrection = Math.max(Math.min((transValue - this.transparency + 0.2) * 5, 1), 0);

            const alpha = this.opacity * transparencyCorrection;

            this.colormapTextureDataArray[i * 4] = Math.round(Math.max(Math.min(alpha * colorData[i].r * 255, 255), 0));
            this.colormapTextureDataArray[i * 4 + 1] = Math.round(
                Math.max(Math.min(alpha * colorData[i].g * 255, 255), 0)
            );
            this.colormapTextureDataArray[i * 4 + 2] = Math.round(
                Math.max(Math.min(alpha * colorData[i].b * 255, 255), 0)
            );
            this.colormapTextureDataArray[i * 4 + 3] = Math.round(Math.max(Math.min(alpha * 255, 255), 0));
        }

        this.gl.activeTexture(this.gl.TEXTURE1);
        this.gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, this.colorMapTexture);
        this.gl.texImage2D(
            WebGLRenderingContext.TEXTURE_2D,
            0,
            this.gl.RGBA,
            GridPlot.ColorMapResolution,
            1,
            0,
            this.gl.RGBA,
            this.gl.UNSIGNED_BYTE,
            this.colormapTextureDataArray
        );

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);

        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
        this.gl.texParameteri(WebGLRenderingContext.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);

        this.gl.generateMipmap(WebGLRenderingContext.TEXTURE_2D);
    }

    private requestRedraw(): void {
        window.requestAnimationFrame(() => this.redraw());
    }

    private redraw(): void {
        if (!this.gl) return;

        this.gl.clear(this.gl.COLOR_BUFFER_BIT);

        if (!this.data) return;

        this.drawGrid();

        const dataUnit = this.data.unit;

        // Update displayed min-max values; convert value from dataUnit to displayUnit
        // Note: this.dataMinimum is the one that is _displayed_, and this.currentMinimum comes from the _data_ source (weirdly enough)
        this.dataMinimum = new UnitValue(this.displayUnit.fromUnit(dataUnit, this.currentMinimum), this.displayUnit);
        this.dataMaximum = new UnitValue(this.displayUnit.fromUnit(dataUnit, this.currentMaximum), this.displayUnit);
    }

    private drawGrid(): void {
        // Select correct program
        this.gl.useProgram(this.shaderProgram);

        // Enable vertex arrays
        this.gl.enableVertexAttribArray(this.vertexDataLocation);
        this.gl.enableVertexAttribArray(this.texCoordDataLocation);

        // Compute coordinates
        const textureWidth = Math.pow(2, Math.ceil(Math.log2(this.bufferedGridDimension.x)));
        const textureHeight = Math.pow(2, Math.ceil(Math.log2(this.bufferedGridDimension.y)));

        const tx = this.bufferedGridDimension.x / textureWidth;
        const ty = this.bufferedGridDimension.y / textureHeight;

        this.gl.uniform4f(
            this.shaderViewportLocation,
            ((this.viewPort.x - this.viewPortBounds.x) / this.viewPortBounds.width) * tx,
            ((this.viewPort.y - this.viewPortBounds.y - (this.viewPortBounds.height - this.viewPort.height)) /
                this.viewPortBounds.height) *
                ty,
            this.viewPort.width / this.viewPortBounds.width,
            this.viewPort.height / this.viewPortBounds.height
        );

        // Set data
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer);
        this.gl.vertexAttribPointer(this.vertexDataLocation, 2, this.gl.FLOAT, false, 0, 0);

        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);
        this.gl.vertexAttribPointer(this.texCoordDataLocation, 2, this.gl.FLOAT, false, 0, 0);

        // Set shader properties
        this.gl.uniform1i(this.hologramTextureLocation, 0);
        this.gl.uniform1i(this.colormapTextureLocation, 1);

        // Draw
        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

        // Disable vertex arrays
        this.gl.disableVertexAttribArray(this.vertexDataLocation);
        this.gl.disableVertexAttribArray(this.texCoordDataLocation);
    }

    private toggleFullScreen(): void {
        if (this.isFullScreen) {
            document.exitFullscreen();
        } else {
            this.controlContainer.requestFullscreen();
        }
    }

    //#endregion
}
